/**
 * API Response Handler
 * Standardizes handling of API responses and errors
 *
 * @module utils/responseHandler
 */

import { handleApiError } from "./errorHandler";

/**
 * Standard API response structure
 * @typedef {Object} ApiResponse
 * @property {boolean} success - Whether the request was successful
 * @property {any} data - Response data
 * @property {string} message - Response message
 * @property {Object} meta - Optional metadata (pagination, etc.)
 */

/**
 * Extract data from API response
 * Handles various response structures from backend
 *
 * @param {Object} response - Axios response object
 * @returns {any} Extracted data
 */
export const extractResponseData = (response) => {
  if (!response) {
    return null;
  }

  // Handle direct response
  if (response.data) {
    // Standard format: { success, data, message }
    if (response.data.success !== undefined) {
      return response.data.data || response.data;
    }

    // Direct data format
    return response.data;
  }

  return response;
};

/**
 * Check if response indicates success
 * @param {Object} response - Axios response object
 * @returns {boolean} True if successful
 */
export const isSuccessResponse = (response) => {
  if (!response) {
    return false;
  }

  // Check status code
  if (response.status >= 200 && response.status < 300) {
    // Check success flag if present
    if (response.data?.success !== undefined) {
      return response.data.success === true;
    }
    return true;
  }

  return false;
};

/**
 * Extract error message from response
 * @param {Object} error - Error object (Axios error or API error)
 * @returns {string} Error message
 */
export const extractErrorMessage = (error) => {
  return handleApiError(error);
};

/**
 * Extract pagination metadata from response
 * @param {Object} response - Axios response object
 * @returns {Object} Pagination metadata
 */
export const extractPagination = (response) => {
  const data = response?.data;

  if (!data) {
    return null;
  }

  // Check for pagination in various formats
  if (data.pagination) {
    return data.pagination;
  }

  if (data.meta?.pagination) {
    return data.meta.pagination;
  }

  // Extract from response if available
  const pagination = {};

  if (data.page !== undefined) pagination.page = data.page;
  if (data.limit !== undefined) pagination.limit = data.limit;
  if (data.total !== undefined) pagination.total = data.total;
  if (data.totalPages !== undefined) pagination.totalPages = data.totalPages;

  return Object.keys(pagination).length > 0 ? pagination : null;
};

/**
 * Transform response to standard format
 * @param {Object} response - Axios response object
 * @returns {ApiResponse} Standardized response
 */
export const transformResponse = (response) => {
  return {
    success: isSuccessResponse(response),
    data: extractResponseData(response),
    message: response?.data?.message || "Success",
    meta: {
      pagination: extractPagination(response),
      timestamp: new Date().toISOString(),
    },
  };
};

/**
 * Transform error to standard format
 * @param {Object} error - Error object
 * @returns {ApiResponse} Standardized error response
 */
export const transformError = (error) => {
  return {
    success: false,
    data: null,
    message: extractErrorMessage(error),
    error: {
      code: error?.response?.status,
      details: error?.response?.data,
    },
  };
};

/**
 * Handle API response with automatic error handling
 * @param {Promise} promise - Axios request promise
 * @returns {Promise<ApiResponse>} Standardized response
 */
export const handleResponse = async (promise) => {
  try {
    const response = await promise;
    return transformResponse(response);
  } catch (error) {
    return transformError(error);
  }
};

/**
 * Create a response handler with custom transform functions
 * @param {Function} successTransform - Custom success transform
 * @param {Function} errorTransform - Custom error transform
 * @returns {Function} Response handler
 */
export const createResponseHandler = (
  successTransform = transformResponse,
  errorTransform = transformError
) => {
  return async (promise) => {
    try {
      const response = await promise;
      return successTransform(response);
    } catch (error) {
      return errorTransform(error);
    }
  };
};

/**
 * Unwrap response data (throws on error)
 * Useful when you want to handle errors at a higher level
 *
 * @param {Object} response - Axios response object
 * @returns {any} Response data
 * @throws {Error} If response indicates failure
 */
export const unwrapResponse = (response) => {
  if (!isSuccessResponse(response)) {
    throw new Error(
      response?.data?.message || "Request failed"
    );
  }

  return extractResponseData(response);
};

/**
 * Check if error is a specific HTTP status code
 * @param {Object} error - Error object
 * @param {number} statusCode - HTTP status code to check
 * @returns {boolean} True if error matches status code
 */
export const isErrorStatus = (error, statusCode) => {
  return error?.response?.status === statusCode;
};

/**
 * Check if error is authentication error (401)
 * @param {Object} error - Error object
 * @returns {boolean} True if authentication error
 */
export const isAuthError = (error) => {
  return isErrorStatus(error, 401);
};

/**
 * Check if error is permission error (403)
 * @param {Object} error - Error object
 * @returns {boolean} True if permission error
 */
export const isPermissionError = (error) => {
  return isErrorStatus(error, 403);
};

/**
 * Check if error is not found error (404)
 * @param {Object} error - Error object
 * @returns {boolean} True if not found error
 */
export const isNotFoundError = (error) => {
  return isErrorStatus(error, 404);
};

/**
 * Check if error is validation error (422)
 * @param {Object} error - Error object
 * @returns {boolean} True if validation error
 */
export const isValidationError = (error) => {
  return isErrorStatus(error, 422) || isErrorStatus(error, 400);
};

export default {
  extractResponseData,
  isSuccessResponse,
  extractErrorMessage,
  extractPagination,
  transformResponse,
  transformError,
  handleResponse,
  createResponseHandler,
  unwrapResponse,
  isErrorStatus,
  isAuthError,
  isPermissionError,
  isNotFoundError,
  isValidationError,
};

